import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

export function ProductsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all'|'active'|'inactive'>('all');
  const [view, setView] = useState<'list'|'grid'>('list');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['products', q, page, statusFilter],
    queryFn: () => inventoryApi.listProducts({ q: q || undefined, page, pageSize: 50, status: statusFilter === 'all' ? undefined : statusFilter }),
  });

  const createMut = useMutation({
    mutationFn: (p: any) => inventoryApi.createProduct(p),
    onSuccess: () => { toast.success('Product created'); qc.invalidateQueries({ queryKey: ['products'] }); setShowCreate(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => inventoryApi.updateProduct(id, payload),
    onSuccess: () => { toast.success('Product updated'); qc.invalidateQueries({ queryKey: ['products'] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => inventoryApi.deleteProduct(id),
    onSuccess: () => { toast.success('Product deleted'); qc.invalidateQueries({ queryKey: ['products'] }); setDeletingId(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = useMemo(() => {
    const rows = data?.data ?? [];
    return {
      total: data?.pagination?.total ?? rows.length,
      active: rows.filter((p: any) => p.status === 'active').length,
      lowStock: rows.filter((p: any) => parseFloat(p.stock_qty) > 0 && parseFloat(p.stock_qty) <= 10).length,
      outOfStock: rows.filter((p: any) => parseFloat(p.stock_qty) <= 0).length,
    };
  }, [data]);

  const products = data?.data ?? [];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader eyebrow="Inventory" title="Products"
        subtitle="Your product catalog — SKUs, pricing, and stock levels."
        actions={
          <>
            <Link to="/inventory/bulk" className="btn-secondary">Bulk import</Link>
            <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New product</button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[['Total', kpis.total, 'default'],['Active', kpis.active, 'sage'],['Low stock', kpis.lowStock, 'amber'],['Out of stock', kpis.outOfStock, 'danger']].map(([label, value, accent]) => (
          <div key={label as string} className="card p-5 relative overflow-hidden">
            <div className={`absolute top-0 left-0 right-0 h-0.5 ${{ default:'bg-ink-200', sage:'bg-sage-500', amber:'bg-amber-500', danger:'bg-danger' }[accent as string]}`} />
            <div className="eyebrow text-ink-500">{label}</div>
            <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">{(value as number).toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex-1 min-w-48 flex items-center gap-2 px-3">
          <SearchIcon />
          <input type="text" placeholder="Search by SKU, name, or barcode…" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-ink-400 py-2 min-w-0" />
        </div>
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1">
          {(['all','active','inactive'] as const).map((k) => (
            <button key={k} onClick={() => { setStatusFilter(k); setPage(1); }}
              className={clsx('px-3 py-1 text-xs font-semibold rounded-pill transition-all capitalize',
                statusFilter === k ? 'bg-canvas-elevated text-ink-900 shadow-xs' : 'text-ink-600 hover:text-ink-900')}>
              {k === 'all' ? 'All' : k}
            </button>
          ))}
        </div>
        <div className="flex gap-1 border border-ink-200 rounded-lg p-0.5">
          {(['list','grid'] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={clsx('px-2 py-1 rounded text-xs font-medium transition-all',
                view === v ? 'bg-canvas-subtle text-ink-900' : 'text-ink-500 hover:text-ink-900')}>
              {v === 'list' ? '☰' : '⊞'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? <div className="card"><LoadingState rows={8} /></div>
        : error ? <ErrorState error={error} />
        : !products.length ? (
          <div className="card">
            <EmptyState message={q ? 'No products match' : 'No products yet'}
              hint={q ? 'Try a different search.' : 'Create your first product to start building your catalog.'}
              action={!q && <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New product</button>}
            />
          </div>
        ) : view === 'list' ? (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th><th>Category</th>
                    <th className="text-right">Price</th><th className="text-right">Cost</th>
                    <th className="text-right">Stock</th><th>Status</th>
                    <th className="text-right w-48">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p: any) => {
                    const stockQty = parseFloat(p.stock_qty);
                    const stockTone = stockQty <= 0 ? 'text-danger' : stockQty <= 10 ? 'text-amber-600' : 'text-ink-700';
                    return (
                      <tr key={p.id}>
                        <td>
                          <Link to={`/inventory/products/${p.id}`} className="flex items-center gap-3 group">
                            <ProductThumb name={p.name} />
                            <div className="min-w-0">
                              <div className="font-semibold text-ink-900 group-hover:text-sage-700 transition-colors truncate">{p.name}</div>
                              <div className="font-mono text-xs text-ink-500">{p.sku}</div>
                            </div>
                          </Link>
                        </td>
                        <td className="text-sm text-ink-600">{p.category_name ?? '—'}</td>
                        <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtCurrency(parseFloat(p.base_price))}</td>
                        <td className="text-right font-mono text-sm text-ink-500 tabular-nums">{fmtCurrency(parseFloat(p.cost_price))}</td>
                        <td className={`text-right font-mono font-semibold tabular-nums ${stockTone}`}>{stockQty.toFixed(0)}</td>
                        <td><StatusBadge status={p.status} /></td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                              onClick={() => setEditing(p)}>Edit</button>
                            <button className="text-xs font-medium px-2 py-1 rounded text-danger hover:bg-red-50 transition-colors"
                              onClick={() => setDeletingId(p.id)}>Delete</button>
                            <Link to={`/inventory/products/${p.id}`} className="text-ink-400 hover:text-ink-700 px-1"><ChevronIcon /></Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {data?.pagination && data.pagination.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-ink-100 flex items-center justify-between">
                <span className="text-xs text-ink-500">Page {page} of {data.pagination.totalPages}</span>
                <div className="flex gap-2">
                  <button className="btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</button>
                  <button className="btn-secondary btn-sm" disabled={page >= data.pagination.totalPages} onClick={() => setPage(page + 1)}>Next →</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {products.map((p: any) => (
              <div key={p.id} className="card overflow-hidden group">
                <Link to={`/inventory/products/${p.id}`} className="block">
                  <div className="aspect-square bg-canvas-subtle flex items-center justify-center">
                    <ProductThumb name={p.name} size="lg" />
                  </div>
                  <div className="p-3">
                    <div className="font-semibold text-ink-900 truncate text-sm group-hover:text-sage-700 transition-colors">{p.name}</div>
                    <div className="font-mono text-xs text-ink-500 mt-0.5">{p.sku}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono font-bold text-ink-900">{fmtCurrency(parseFloat(p.base_price))}</span>
                      <StatusBadge status={p.status} />
                    </div>
                  </div>
                </Link>
                <div className="px-3 pb-3 flex gap-1">
                  <button className="flex-1 text-xs font-medium py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600 border border-ink-200"
                    onClick={() => setEditing(p)}>Edit</button>
                  <button className="text-xs font-medium px-2 py-1 rounded text-danger hover:bg-red-50 transition-colors border border-red-200"
                    onClick={() => setDeletingId(p.id)}>Del</button>
                </div>
              </div>
            ))}
          </div>
        )}

      {showCreate && <ProductModal onSubmit={(p) => createMut.mutate(p)} onClose={() => setShowCreate(false)} loading={createMut.isPending} />}
      {editing && <ProductModal initial={editing} onSubmit={(p) => updateMut.mutate({ id: editing.id, payload: p })} onClose={() => setEditing(null)} loading={updateMut.isPending} />}
      {deletingId && (
        <Modal
          onClose={() => setDeletingId(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Inventory · Products</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete product?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">This product and all its data will be permanently removed. This cannot be undone.</p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setDeletingId(null)}>Cancel</button>
            <button className="btn-primary !bg-danger hover:!bg-red-700" disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate(deletingId)}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete product'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ProductThumb({ name, size = 'sm' }: { name: string; size?: 'sm'|'lg' }) {
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = ['bg-sage-100 text-sage-600','bg-blue-100 text-blue-600','bg-amber-100 text-amber-700','bg-purple-100 text-purple-600','bg-teal-100 text-teal-600'];
  const sz = size === 'lg' ? 'w-20 h-20 text-2xl' : 'w-9 h-9 text-sm';
  return <div className={clsx('rounded-lg flex items-center justify-center font-bold flex-shrink-0', sz, tones[hash % tones.length])}>{name[0]?.toUpperCase() ?? '?'}</div>;
}

function ProductModal({ initial, onSubmit, onClose, loading }: {
  initial?: any; onSubmit: (p: any) => void; onClose: () => void; loading: boolean;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    sku: initial?.sku ?? '',
    name: initial?.name ?? '',
    barcode: initial?.barcode ?? '',
    basePrice: initial?.base_price ? parseFloat(initial.base_price) : 0,
    costPrice: initial?.cost_price ? parseFloat(initial.cost_price) : 0,
    taxClassCode: initial?.tax_class_code ?? 'STD',
  });
  const margin = form.basePrice > 0 && form.costPrice > 0
    ? ((form.basePrice - form.costPrice) / form.basePrice * 100).toFixed(1)
    : null;
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Inventory</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit product' : 'New product'}</h2>
        {!isEdit && <p className="text-sm text-ink-500 mt-1">Create a new SKU. Variants and stock can be added later.</p>}
      </div>
      <div className="px-6 py-5 space-y-4">
        {!isEdit && (
          <div>
            <label className="label">SKU <span className="text-danger">*</span></label>
            <input className="input font-mono" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="SHIRT-BLUE-M" />
          </div>
        )}
        <div>
          <label className="label">Product name <span className="text-danger">*</span></label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Blue cotton shirt, medium" />
        </div>
        <div>
          <label className="label">Barcode <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input font-mono" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Selling price</label>
            <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 text-sm">$</span>
              <input type="number" step="0.01" className="input pl-7 tabular-nums" value={form.basePrice}
                onChange={(e) => setForm({ ...form, basePrice: parseFloat(e.target.value) || 0 })} /></div>
          </div>
          <div>
            <label className="label">Cost</label>
            <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 text-sm">$</span>
              <input type="number" step="0.01" className="input pl-7 tabular-nums" value={form.costPrice}
                onChange={(e) => setForm({ ...form, costPrice: parseFloat(e.target.value) || 0 })} /></div>
          </div>
        </div>
        {margin && <div className="text-xs text-ink-500">Margin: <span className="font-semibold text-sage-700">{margin}%</span></div>}
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading || !form.name || (!isEdit && !form.sku)}
          onClick={() => onSubmit(isEdit
            ? { name: form.name, barcode: form.barcode || undefined, basePrice: form.basePrice, costPrice: form.costPrice }
            : { sku: form.sku, name: form.name, barcode: form.barcode || undefined, basePrice: form.basePrice, costPrice: form.costPrice, taxClassCode: form.taxClassCode })}>
          {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create product')}
        </button>
      </div>
    </Modal>
  );
}

const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon() { return <svg {...ip} className="text-ink-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function ChevronIcon() { return <svg {...ip} width="14" height="14"><polyline points="9 18 15 12 9 6"/></svg>; }
